import React, { useState, useEffect } from 'react';
import dayjs from 'op-utils/dayjs';

import { DATATYPE } from 'shared-components/components/UIFormComponents/SimpleTable';
import { getRemSize } from 'shared-components/StyledComponents/functions';
import { Stack } from '@mui/material';
import { KeyboardArrowDown, KeyboardArrowUp } from '@mui/icons-material';
import { FindInPageOutlined as FindInPageOutlinedIcon } from '@mui/icons-material';
import { styled, Typography } from '@mui/material';

interface StyledRowProps {
  active: boolean;
}

interface TableHeaderProps {
  title: string;
  key: string;
  sort?: boolean;
  type: string;
}

interface TableDataProps {
  title?: string;
  subtitle?: string;
  id?: string;
  key: string;
}

interface Props {
  tableHeaders: TableHeaderProps[];
  tableData?: TableDataProps[][];
  dateFormat?: string;
  onRowClick?: (item: TableDataProps[]) => void;
  defaultSelectedIndex?: number;
  testId?: string;
}

const TableWrapper = styled('div')`
  height: 100%;
  display: flex;
  flex-direction: column;
  overflow: hidden;
  min-height: 0;
`;

const TableContainer = styled('div')`
  flex: 1;
  overflow: hidden;
  min-height: 0;
  position: relative;
  display: flex;
  flex-direction: column;
`;

const Table = styled('table')`
  width: 100%;
  border-collapse: collapse;
  table-layout: fixed;
`;

const TableHead = styled('thead')`
  position: sticky;
  top: 0;
  z-index: 1;
  background-color: ${(props) => props.theme.palette.grey[100]};
`;

const TableHeaderRow = styled('tr')`
  display: table-row;
`;

const TableHeader = styled('th')`
  padding: 14px 14px 12px;
  text-align: left;
  color: ${(props) => props.theme.palette.text.primary};
  background-color: ${(props) => props.theme.palette.grey[100]};
  font-weight: 600;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;

  &:last-of-type {
    text-align: right;
  }

  @media (max-width: 768px) {
    padding: 10px 8px;
    font-size: ${getRemSize(14)};
  }
`;

const TableBodyWrapper = styled('div')`
  flex: 1;
  overflow-y: auto;
  overflow-x: hidden;
  min-height: 0;

  /* Custom scrollbar styling */
  &::-webkit-scrollbar {
    width: 8px;
  }

  &::-webkit-scrollbar-track {
    background: ${(props) => props.theme.palette.grey[200]};
    border-radius: 4px;
  }

  &::-webkit-scrollbar-thumb {
    background: ${(props) => props.theme.palette.grey[400]};
    border-radius: 4px;

    &:hover {
      background: ${(props) => props.theme.palette.grey[500]};
    }
  }

  @media (max-width: 768px) {
    &::-webkit-scrollbar {
      width: 6px;
    }
  }
`;

const TableBody = styled('tbody')`
  display: table-row-group;
`;

const TableRow = styled('tr')<StyledRowProps>`
  cursor: pointer;
  display: table-row;
  height: 60px; /* Fixed row height */
  max-height: 60px;

  &:hover {
    background-color: ${(props) =>
      props.active ? props.theme.palette.action.selected : props.theme.palette.action.hover};
  }
  background-color: ${(props) => (props.active ? props.theme.palette.action.selected : '')};

  @media (max-width: 768px) {
    height: 50px; /* Smaller fixed height on mobile */
    max-height: 50px;
    &:hover {
      background-color: ${(props) =>
        props.active ? props.theme.palette.action.selected : props.theme.palette.action.hover};
    }
  }
`;

const TableData = styled('td')<StyledRowProps>`
  padding: 12px 16px;
  font-size: ${getRemSize(16)};
  vertical-align: middle;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  border-bottom: 1px solid ${(props) => props.theme.palette.divider};

  @media (max-width: 768px) {
    padding: 8px 12px;
    font-size: ${getRemSize(14)};
  }
`;

const SubTableData = styled('div')`
  padding-top: 4px;
  font-size: ${getRemSize(13)};
  color: ${(props) => props.theme.palette.grey[600]};
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;

  @media (max-width: 768px) {
    font-size: ${getRemSize(12)};
  }
`;

const NoDataContainer = styled('div')`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  height: 100%;
  min-height: 200px;
`;

const SORT_TYPES = {
  ASC: 'ASC',
  DESC: 'DESC',
};

const NO_DATA = 'No results found';

const TableComponent = (props: Props): JSX.Element => {
  const { tableHeaders, tableData, dateFormat = 'DD-MM-YYYY', onRowClick, defaultSelectedIndex, testId } = props;
  const [sortState, setSortState] = useState({ key: 'observationDate', order: SORT_TYPES.DESC, type: DATATYPE.DATE });
  const [activeItem, setActiveItem] = useState<any>({ id: null });

  const sortedData =
    tableData &&
    [...tableData].sort((a: any, b: any): number => {
      if (sortState.type === DATATYPE.DATE) {
        const aDate = a?.[1]?.title;
        const bDate = b?.[1]?.title;
        if (!aDate || !bDate) {
          return 0;
        }
        const aDateFormatted = dayjs(aDate);
        const bDateFormatted = dayjs(bDate);
        if (aDateFormatted.isAfter(bDateFormatted)) {
          return sortState.order === SORT_TYPES.DESC ? -1 : 1;
        } else if (aDateFormatted.isBefore(bDateFormatted)) {
          return sortState.order === SORT_TYPES.DESC ? 1 : -1;
        }
        return 0;
      }
      return 0;
    });

  const onSortToggle = (header: TableHeaderProps): void => {
    const sortOrder = sortState.order === SORT_TYPES.DESC ? SORT_TYPES.ASC : SORT_TYPES.DESC;
    setSortState({ key: header.key, order: sortOrder, type: header.type });
  };

  useEffect(() => {
    const sortedDataExists = sortedData && sortedData.length;
    if (
      sortedDataExists &&
      defaultSelectedIndex !== undefined &&
      defaultSelectedIndex >= 0 &&
      sortedData !== undefined &&
      sortedData[defaultSelectedIndex][0]
    ) {
      setActiveItem(sortedData && sortedData[defaultSelectedIndex][0]);

      if (onRowClick && sortedData && sortedData[defaultSelectedIndex]) {
        onRowClick(sortedData[defaultSelectedIndex]);
      }
    }
  }, []);

  if (tableData && tableData.length && sortedData) {
    return (
      <TableWrapper data-test-id={testId}>
        <TableContainer>
          <TableBodyWrapper data-test-id={`${testId}-body`}>
            <Table>
              <TableHead>
                <TableHeaderRow>
                  {tableHeaders.map((header) => (
                    <TableHeader key={header.key}>
                      <Stack direction="row" alignItems="center" spacing={1}>
                        {header.hasOwnProperty('sort') && (
                          <Stack
                            onClick={() => {
                              onSortToggle(header);
                            }}
                            data-testid={`${header.title}-sort`}
                            sx={{ cursor: 'pointer' }}>
                            <KeyboardArrowUp
                              color={
                                sortState['key'] === header.key && sortState.order === SORT_TYPES.ASC
                                  ? 'primary'
                                  : 'secondary'
                              }
                              sx={{ marginBottom: '-10px' }}
                            />
                            <KeyboardArrowDown
                              color={
                                sortState['key'] === header.key && !(sortState.order === SORT_TYPES.ASC)
                                  ? 'primary'
                                  : 'secondary'
                              }
                            />
                          </Stack>
                        )}
                        <Typography variant="subtitle1" sx={{ overflow: 'hidden', textOverflow: 'ellipsis' }}>
                          {header.title}
                        </Typography>
                      </Stack>
                    </TableHeader>
                  ))}
                </TableHeaderRow>
              </TableHead>
              <TableBody>
                {sortedData.map((item, index) => {
                  const styledProps = {
                    active: item[0].id === activeItem.id,
                  };
                  return (
                    <TableRow
                      data-test-id={`${testId}-row-${index}`}
                      key={`${item[0].title}-${index}`}
                      {...styledProps}
                      onClick={() => {
                        setActiveItem(item[0]);
                        if (onRowClick) {
                          onRowClick(item);
                        }
                      }}>
                      {item.map((data, x) => {
                        return (
                          <TableData key={x} {...styledProps}>
                            <div style={{ overflow: 'hidden', textOverflow: 'ellipsis' }}>
                              {data?.key === 'observationDate' ? dayjs(data.title).utc(false).format('L') : data.title}
                            </div>
                            {data.subtitle && <SubTableData>{data.subtitle}</SubTableData>}
                          </TableData>
                        );
                      })}
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </TableBodyWrapper>
        </TableContainer>
      </TableWrapper>
    );
  }
  return (
    <NoDataContainer>
      <FindInPageOutlinedIcon fontSize="large" />
      <Typography paddingTop={2} variant="body1">
        {NO_DATA}
      </Typography>
    </NoDataContainer>
  );
};

export default TableComponent;
